import { ButtonRow, Checkbox, Spinner } from '@timelyapp/react';
import CollapsibleOptionPicker from 'components/CollapsibleOptionPicker';
import ReportQueryDateOptions from 'components/superreports/ReportQueryDateOptions';
import { DateTime } from 'luxon';
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getCurrentAccount } from 'redux/selectors/accounts';
import type { ForecastQueryFiltersContext } from 'routes/forecast/components/ForecastQueryFilters';
import ForecastQueryFilters from 'routes/forecast/components/ForecastQueryFilters';
import {
  getForecastListHeader,
  getForecastLoadingState,
  getForecastSelections,
} from 'routes/forecast/redux/modules/forecast.selectors';
import type { ForecastViewType } from 'routes/forecast/types/forecast';
import type { ThunkDispatch } from 'types/redux';
import { setQuery } from '../redux/modules/forecast.actions';

interface Props {
  activeViewType: ForecastViewType;
  setActiveViewType: (viewType: ForecastViewType) => void;
  context?: ForecastQueryFiltersContext;
  showAvailability: boolean;
  onSetShowAvailability: (showCapacities: boolean) => void;
  showCapacitiesCheckbox: boolean;
  activeView: 'assignee' | 'project';
}

const ForecastListHeader: React.FC<Props> = ({
  activeViewType = 'list',
  setActiveViewType,
  context,
  showAvailability,
  showCapacitiesCheckbox,
  onSetShowAvailability,
  activeView,
}) => {
  const { projects, project, clients, users, teams, query } = useSelector(getForecastListHeader);
  const selections = useSelector(getForecastSelections);
  const account = useSelector(getCurrentAccount);
  const isLoading = useSelector(getForecastLoadingState);
  const dispatch = useDispatch<ThunkDispatch>();

  const handleChangeShowCompleted = useCallback(
    (checked: boolean) => {
      dispatch(setQuery({ showCompleted: checked }));
    },
    [dispatch]
  );

  const handleSetDateFilter = useCallback(
    (changes: { from?: string; to?: string; span?: 'day' | 'week' | 'month' | 'custom' }) => {
      let fromDate = changes.from ?? query.since;
      let toDate = changes.to ?? query.until;
      const span = changes.span ?? query.dateSpan;

      if (fromDate && toDate && span === 'month') {
        const start = DateTime.fromISO(fromDate);
        const end = DateTime.fromISO(toDate);

        const startMonth = start.startOf('month');
        const endMonth = end.startOf('month');
        const monthCount = Math.round(endMonth.diff(startMonth, 'months').months);

        if (monthCount >= 3) {
          if (changes.from !== undefined) {
            toDate = start.plus({ months: 2 }).endOf('month').toISODate()!;
          } else if (changes.to !== undefined) {
            fromDate = end.minus({ months: 2 }).startOf('month').toISODate()!;
          }
        }
      }

      const updates: {
        since?: string | null;
        until?: string | null;
        dateSpan?: 'day' | 'week' | 'month' | 'custom';
      } = {};

      if (changes.from !== undefined) {
        updates.since = fromDate;
      }
      if (changes.to !== undefined) {
        updates.until = toDate;
      }
      if (changes.span !== undefined) {
        updates.dateSpan = changes.span;
      }

      dispatch(setQuery(updates));
    },
    [dispatch, query.since, query.until, query.dateSpan]
  );

  if (selections.forecastIds.length > 0) {
    return null;
  }

  return (
    <ButtonRow>
      <ButtonRow.Left>
        <CollapsibleOptionPicker
          onClick={option => setActiveViewType(option.value)}
          position="bottom"
          options={[
            {
              value: 'timeline' as const,
              name: 'Timeline',
              active: activeViewType === 'timeline',
              'data-testid': 'tasks-view-mode-switcher_timeline',
            },
            {
              value: 'list' as const,
              name: 'List',
              active: activeViewType === 'list',
              'data-testid': 'tasks-view-mode-switcher_list',
            },
          ]}
          size="small"
        />
        {activeViewType === 'list' && query.since && query.until && (
          <>
            <ReportQueryDateOptions
              onSetQuery={handleSetDateFilter}
              enabledModes={['day', 'week', 'month']}
              query={{
                from: query.since,
                to: query.until,
                span: query.dateSpan,
                sort: { by: 'from', direction: 'desc' },
                filters: { userIds: [], teamIds: [] },
              }}
              account={account}
              size="small"
            />
          </>
        )}
        <ForecastQueryFilters
          initialQuery={query}
          query={query}
          projects={projects}
          clients={clients}
          users={users}
          teams={teams}
          context={context}
          activeView={activeView}
          showProjectAssigneesFilter
        />
        {isLoading && (
          <div style={{ display: 'flex', alignItems: 'center', marginLeft: '8px' }}>
            <Spinner size={20} />
          </div>
        )}
      </ButtonRow.Left>
      <ButtonRow.Right>
        <Checkbox
          name="show_completed_tasks"
          label="Completed tasks"
          checked={query.showCompleted}
          onChangeChecked={handleChangeShowCompleted}
        />
        {activeViewType === 'timeline' && showCapacitiesCheckbox && (
          <Checkbox
            name="show_task_avilability"
            label="Availability"
            checked={showAvailability}
            onChangeChecked={onSetShowAvailability}
          />
        )}
      </ButtonRow.Right>
    </ButtonRow>
  );
};

export default ForecastListHeader;
